import { Component, Input, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Budget } from "../Budget";
import { BudgetService } from "../budget.service";
import { S25Util } from "../../../../util/s25-util";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25PipesModule } from "../../../../pipes/s25.pipes.module";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25BudgetCategoryDropdownComponent } from "../components/s25.budget.category.dropdown.component";
import { S25RequestedChangesComponent } from "../../../../standalone/s25.requested.changes.component";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { Workflow } from "../../../workflow/service/Workflow";

@TypeManagerDecorator("s25-ng-budget-line-items")
@Component({
    selector: "s25-ng-budget-line-items",
    template: `
        <div class="line-items-container">
            @if (budgetPlan.workflowStatus === "REQUESTED_CHANGES") {
                <s25-ng-requested-changes
                    [itemTypeId]="BUDGET_PLAN_OBJECT_TYPE"
                    [itemId]="budgetPlan.budgetPlanId"
                ></s25-ng-requested-changes>
            }

            <div class="line-items-header">
                <h4>Budget Line Items for {{ budgetPlan.name }}</h4>
                <s25-ng-button
                    [type]="'primary'"
                    [buttonText]="'+ Add Line Item'"
                    [onClick]="addLineItem"
                    [isLoading]="isAddingLineItem"
                    [loadingText]="'Adding...'"
                ></s25-ng-button>
            </div>

            @if (isLoading) {
                <div class="loading-state">
                    <p>Loading line items...</p>
                </div>
            } @else if (error) {
                <div class="error-state">
                    <p class="error-message">{{ error }}</p>
                    <s25-ng-button [type]="'outline'" [buttonText]="'Retry'" [onClick]="loadLineItems"></s25-ng-button>
                </div>
            } @else if (lineItems && lineItems.length > 0) {
                <table class="line-items-table">
                    <tr class="line-item-row header">
                        <th>Category</th>
                        <th>Description</th>
                        <th>Planned Amount</th>
                        <th>Actual Amount</th>
                        <th>Remaining</th>
                        <th>Actions</th>
                    </tr>
                    @for (lineItem of lineItems; track lineItem.budgetId) {
                        <tr class="line-item-row" [class.editing]="editingLineItemId === lineItem.lineItemId">
                            @if (editingLineItemId === lineItem.lineItemId) {
                                <ng-container
                                    [ngTemplateOutlet]="editRow"
                                    [ngTemplateOutletContext]="{ lineItem: lineItem }"
                                ></ng-container>
                            } @else {
                                <ng-container
                                    [ngTemplateOutlet]="viewRow"
                                    [ngTemplateOutletContext]="{ lineItem: lineItem }"
                                ></ng-container>
                            }
                        </tr>
                    }
                </table>

                <div class="line-items-summary">
                    <div class="summary-row">
                        <span>Total Planned: {{ getTotalPlanned() | currency }}</span>
                        <span>Total Actual: {{ getTotalActual() | currency }}</span>
                        <span>Total Remaining: {{ getTotalRemaining() | currency }}</span>
                    </div>
                </div>
            } @else {
                <div class="no-line-items">
                    <p>No line items found for this budget plan.</p>
                    <p>Click "Add Line Item" to get started.</p>
                </div>
            }
        </div>

        <ng-template #viewRow let-lineItem="lineItem">
            <td [attr.data-label]="'Category'" class="field-container">
                <span>{{ lineItem.categoryName }}</span>
            </td>
            <td [attr.data-label]="'Description'" class="field-container">
                <span>{{ lineItem.description || lineItem.name }}</span>
            </td>
            <td [attr.data-label]="'Planned Amount'" class="field-container amount">
                <span>{{ lineItem.plannedAmount | currency }}</span>
            </td>
            <td [attr.data-label]="'Actual Amount'" class="field-container amount">
                <span>{{ lineItem.actualAmount | currency }}</span>
            </td>
            <td [attr.data-label]="'Remaining'" class="field-container amount">
                <span>{{ lineItem.plannedAmount - lineItem.actualAmount | currency }}</span>
            </td>
            <td [attr.data-label]="'Actions'" class="field-container actions">
                <s25-ng-button
                    [type]="'outline'"
                    [buttonText]="'Edit'"
                    [onClick]="editLineItem"
                    [onClickArgs]="[lineItem]"
                    [disabled]="isProcessing"
                ></s25-ng-button>
                <s25-ng-button
                    [type]="'danger--outline'"
                    [buttonText]="'Delete'"
                    [onClick]="deleteLineItem"
                    [onClickArgs]="[lineItem]"
                    [disabled]="isProcessing"
                ></s25-ng-button>
            </td>
        </ng-template>

        <ng-template #editRow let-lineItem="lineItem">
            <td [attr.data-label]="'Category'" class="field-container">
                <s25-budget-category-dropdown
                    [chosen]="editingValues.selectedCategory"
                    [hasNoneItem]="true"
                    [noneItemLabel]="'No Category'"
                    (chosenChange)="onCategoryChange($event)"
                ></s25-budget-category-dropdown>
            </td>
            <td [attr.data-label]="'Description'" class="field-container">
                <input
                    type="text"
                    class="edit-input"
                    [(ngModel)]="editingValues.description"
                    placeholder="Description"
                    (keydown.escape)="cancelEdit()"
                />
            </td>
            <td [attr.data-label]="'Planned Amount'" class="field-container amount">
                <input
                    type="number"
                    class="edit-input amount-input"
                    [(ngModel)]="editingValues.plannedAmount"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    (keydown.escape)="cancelEdit()"
                />
            </td>
            <td [attr.data-label]="'Actual Amount'" class="field-container amount">
                <input
                    type="number"
                    class="edit-input amount-input"
                    [(ngModel)]="editingValues.actualAmount"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    (keydown.escape)="cancelEdit()"
                />
            </td>
            <td [attr.data-label]="'Remaining'" class="field-container amount">
                <span>{{ (editingValues.plannedAmount || 0) - (editingValues.actualAmount || 0) | currency }}</span>
            </td>
            <td [attr.data-label]="'Actions'" class="field-container actions">
                <s25-ng-button
                    [type]="'primary'"
                    [buttonText]="'Save'"
                    [onClick]="saveLineItem"
                    [onClickArgs]="[lineItem]"
                    [disabled]="isProcessing"
                ></s25-ng-button>
                <s25-ng-button
                    [type]="'outline'"
                    [buttonText]="'Cancel'"
                    [onClick]="cancelEdit"
                    [disabled]="isProcessing"
                ></s25-ng-button>
            </td>
        </ng-template>
    `,
    styles: `
        .line-items-container {
            padding: 1.5rem;
            background: #fafafa;
            border-radius: 8px;
            margin: 0.5rem 0;
            overflow: visible;
        }

        .line-items-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .line-items-header h4 {
            margin: 0;
            color: #333;
            font-size: 1.1rem;
            font-weight: 600;
        }

        .loading-state,
        .error-state,
        .no-line-items {
            text-align: center;
            padding: 2rem;
            color: #666;
        }

        .error-state {
            background: #fff3f3;
            border: 1px solid #ffcdd2;
            border-radius: 8px;
        }

        .error-message {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        .line-items-table {
            border: 1px solid #e5e5e5;
            border-radius: 4px;
            overflow: hidden;
        }

        .line-item-row {
            display: grid;
            grid-template-columns: 1fr 2fr 1fr 1fr 1fr 120px;
            gap: 1rem;
            padding: 0.75rem 1rem;
            align-items: center;
        }

        .line-item-row.header {
            background: #f5f5f5;
            font-weight: 600;
            color: #333;
            border-bottom: 1px solid #e5e5e5;
        }

        .line-item-row:not(.header) {
            border-bottom: 1px solid #f0f0f0;
        }

        .line-item-row:not(.header):last-child {
            border-bottom: none;
        }

        .line-item-row:not(.header):hover {
            background: #f9f9f9;
        }

        .line-item-row.editing {
            background: #e3f2fd;
            border-left: 3px solid #2196f3;
        }

        .line-item-row .amount {
            text-align: right;
            font-family: "Courier New", monospace;
            font-weight: 500;
        }

        .line-item-row .actions {
            display: flex;
            gap: 0.5rem;
        }

        /* Input field styling */
        .field-container {
            display: flex;
            align-items: center;
            width: 100%;
            position: relative;
            overflow: visible;
        }

        .edit-input {
            width: 100%;
            padding: 0.375rem 0.5rem;
            border: 1px solid #ccc;
            border-radius: 3px;
            font-size: 0.9rem;
            background: white;
            transition: border-color 0.2s;
        }

        .edit-input:focus {
            outline: none;
            border-color: #2196f3;
            box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }

        .amount-input {
            text-align: right;
            font-family: "Courier New", monospace;
        }

        /* Ensure spans in field containers take full width */
        .field-container span {
            width: 100%;
        }

        /* Ensure dropdown can extend beyond table boundaries */
        .line-item-row {
            position: relative;
            overflow: visible;
        }

        .line-items-table {
            overflow: visible;
        }

        /* Specific styling for category dropdown cell */
        .field-container:has(s25-budget-category-dropdown) {
            z-index: 100;
            overflow: visible;
        }

        /* Alternative selector for older browsers that don't support :has() */
        .field-container s25-budget-category-dropdown {
            position: relative;
            z-index: 100;
        }

        .line-items-summary {
            margin-top: 1rem;
            padding: 1rem;
            background: white;
            border-radius: 4px;
            border: 1px solid #e5e5e5;
        }

        .summary-row {
            display: flex;
            justify-content: space-between;
            font-weight: 600;
            color: #333;
        }

        .summary-row span {
            font-family: "Courier New", monospace;
        }

        .no-line-items p {
            margin: 0.5rem 0;
        }

        /* Responsive design */
        @media (max-width: 768px) {
            .line-items-container {
                padding: 1rem;
            }

            .line-items-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .line-item-row {
                grid-template-columns: 1fr;
                gap: 0.5rem;
            }

            .line-item-row.header {
                display: none;
            }

            .line-item-row:not(.header) .field-container {
                display: flex;
                justify-content: space-between;
                padding: 0.25rem 0;
            }

            .line-item-row:not(.header) .field-container:before {
                content: attr(data-label) ":";
                font-weight: 600;
                margin-right: 1rem;
                flex-shrink: 0;
            }

            .line-item-row:not(.header) .field-container .edit-input {
                flex: 1;
                margin-left: auto;
                max-width: 60%;
            }

            .summary-row {
                flex-direction: column;
                gap: 0.5rem;
            }

            .actions {
                justify-content: center;
            }
        }
    `,
    standalone: true,
    imports: [
        S25ButtonComponent,
        S25PipesModule,
        FormsModule,
        CommonModule,
        S25BudgetCategoryDropdownComponent,
        S25RequestedChangesComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetLineItemsComponent implements OnInit {
    @Input() budgetPlan: Budget.BudgetPlan;
    @Input() initialLineItems?: Budget.BudgetLineItem[];

    lineItems: Budget.BudgetLineItem[] = [];
    isLoading = false;
    isAddingLineItem = false;
    isProcessing = false;
    editingLineItemId: number | null = null;
    editingValues: Partial<Budget.BudgetLineItem> & { selectedCategory?: DropDownItem } = {};
    error: string | null = null;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        if (this.initialLineItems) {
            this.lineItems = [...this.initialLineItems];
        } else {
            await this.loadLineItems();
        }
    }

    @Bind
    async loadLineItems(): Promise<void> {
        if (!this.budgetPlan?.budgetPlanId) {
            return;
        }

        this.isLoading = true;
        this.error = null;
        this.cd.detectChanges();

        const [data, error] = await S25Util.Maybe(BudgetService.getBudgetLineItems(this.budgetPlan.budgetPlanId));

        if (error) {
            this.error = error instanceof Error ? error.message : "Failed to load line items";
        } else {
            this.lineItems = data || [];
        }
        this.isLoading = false;
        this.cd.detectChanges();
    }

    @Bind
    async addLineItem(): Promise<void> {
        let newLineItem: Budget.BudgetLineItem = {
            lineItemId: undefined,
            amount: 0,
            categoryId: 0,
            committedAmount: 0,
            createUser: "",
            createDate: undefined,
            currency: "",
            lastModUser: "",
            lastModDate: undefined,
            transactionIds: [],
            budgetId: this.budgetPlan.orgBudgetId,
            budgetPlanId: this.budgetPlan.budgetPlanId,
            categoryName: "New Category",
            name: "New Line Item",
            description: "",
            plannedAmount: 0,
            actualAmount: 0,
        };

        this.lineItems.push(newLineItem);
    }

    @Bind
    editLineItem(lineItem: Budget.BudgetLineItem): void {
        if (this.isProcessing) return;

        this.editingLineItemId = lineItem.lineItemId;
        // Copy current values to editing state
        this.editingValues = {
            categoryName: lineItem.categoryName,
            categoryId: lineItem.categoryId,
            description: lineItem.description || lineItem.name,
            plannedAmount: lineItem.plannedAmount,
            actualAmount: lineItem.actualAmount,
            selectedCategory: lineItem.categoryId
                ? {
                      val: lineItem.categoryId.toString(),
                      txt: lineItem.categoryName || "",
                      itemId: lineItem.categoryId,
                      itemName: lineItem.categoryName || "",
                  }
                : undefined,
        };
        this.cd.detectChanges();
    }

    @Bind
    onCategoryChange(selectedCategory: DropDownItem): void {
        this.editingValues.selectedCategory = selectedCategory;
        if (selectedCategory) {
            this.editingValues.categoryId = selectedCategory.itemId as number;
            this.editingValues.categoryName = selectedCategory.itemName;
        } else {
            this.editingValues.categoryId = undefined;
            this.editingValues.categoryName = undefined;
        }
        this.cd.detectChanges();
    }

    @Bind
    cancelEdit(): void {
        this.editingLineItemId = null;
        this.editingValues = {};
        this.cd.detectChanges();
    }

    @Bind
    async saveLineItem(lineItem: Budget.BudgetLineItem): Promise<void> {
        if (this.isProcessing || !this.editingValues) return;

        if (!this.editingValues.description?.trim()) {
            S25Util.showError("Description is required");
            return;
        }

        if (
            this.editingValues.plannedAmount === null ||
            this.editingValues.plannedAmount === undefined ||
            this.editingValues.plannedAmount < 0
        ) {
            S25Util.showError("Planned amount must be 0 or greater");
            return;
        }

        if (
            this.editingValues.actualAmount === null ||
            this.editingValues.actualAmount === undefined ||
            this.editingValues.actualAmount < 0
        ) {
            S25Util.showError("Actual amount must be 0 or greater");
            return;
        }

        this.isProcessing = true;
        this.cd.detectChanges();

        try {
            // Create updated line item
            const updatedLineItem: Budget.BudgetLineItem = {
                ...lineItem,
                categoryId: this.editingValues.categoryId || 0,
                categoryName: this.editingValues.categoryName || "",
                description: this.editingValues.description!,
                plannedAmount: this.editingValues.plannedAmount!,
                actualAmount: this.editingValues.actualAmount!,
            };

            // Save to server
            const [success, error] = await S25Util.Maybe(
                BudgetService.updateBudgetLineItem(lineItem.lineItemId, updatedLineItem),
            );

            if (error) {
                throw error;
            }

            const index = this.lineItems.findIndex((item) => item.lineItemId === lineItem.lineItemId);
            if (index !== -1) {
                this.lineItems[index] = updatedLineItem;
            }
            this.cancelEdit();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to save line item";
            S25Util.showError(error, errorMessage);
        } finally {
            this.isProcessing = false;
            this.cd.detectChanges();
        }
    }

    async deleteLineItem(lineItem: Budget.BudgetLineItem): Promise<void> {
        if (this.isProcessing) return;

        const confirmed = confirm(`Are you sure you want to delete the line item "${lineItem.name}"?`);
        if (!confirmed) return;

        this.isProcessing = true;
        this.cd.detectChanges();

        try {
            const [success, error] = await S25Util.Maybe(BudgetService.deleteBudgetLineItem(lineItem.lineItemId));

            if (error) {
                throw error;
            }

            // Remove from local array
            this.lineItems = this.lineItems.filter((item) => item.budgetId !== lineItem.budgetId);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to delete line item";
            S25Util.showError(error, errorMessage);
        } finally {
            this.isProcessing = false;
            this.cd.detectChanges();
        }
    }

    // Helper methods for calculations
    getTotalPlanned(): number {
        return this.lineItems.reduce((total, item) => total + (item.plannedAmount || 0), 0);
    }

    getTotalActual(): number {
        return this.lineItems.reduce((total, item) => total + (item.actualAmount || 0), 0);
    }

    getTotalRemaining(): number {
        return this.lineItems.reduce(
            (total, item) => total + ((item.plannedAmount || 0) - (item.actualAmount || 0)),
            0,
        );
    }

    // Public method to refresh line items from parent
    async refresh(): Promise<void> {
        await this.loadLineItems();
    }

    readonly BUDGET_PLAN_OBJECT_TYPE = Workflow.BUDGET_PLAN_OBJECT_TYPE;
}
